import { useState, useEffect, RefObject } from 'react';

interface UseTextOverflowDetectionResult {
    isOverflowing: boolean;
    hasCheckedOverflow: boolean;
}

/**
 * Custom hook to detect text overflow in an element
 * @param subtitleRef - Reference to the element to check for overflow
 * @param subtitle - The subtitle text content (dependency to trigger re-check)
 * @returns Object containing overflow state and check status
 */
export const useTextOverflowDetection = (
    subtitleRef: RefObject<HTMLElement>,
    subtitle?: string
): UseTextOverflowDetectionResult => {
    const [isOverflowing, setIsOverflowing] = useState(true);
    const [hasCheckedOverflow, setHasCheckedOverflow] = useState(false);

    useEffect(() => {
        setHasCheckedOverflow(false);
        setIsOverflowing(true);

        const element = subtitleRef.current;

        const checkOverflow = () => {
            if (!element || !subtitle) {
                setIsOverflowing(false);
                setHasCheckedOverflow(true);
                return;
            }

            const hasOverflow = element.scrollWidth > element.clientWidth;

            setIsOverflowing(hasOverflow);
            setHasCheckedOverflow(true);
        };

        const timeoutId = setTimeout(checkOverflow, 0);

        window.addEventListener('resize', checkOverflow);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', checkOverflow);
        };
    }, [subtitle, subtitleRef]);

    return {
        isOverflowing,
        hasCheckedOverflow,
    };
};
